import { isOperationCalibrationEnabled } from '@framework/utils/operationCalibration';
import { OperationCalibrationPanel } from './OperationCalibrationPanel';
import { ActionLogEventParamsContextProvider } from '@framework/action-log';
import { FullScreenLoading } from '@framework/ui';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { NotFoundPage } from '../NotFoundPage';
import { ViewModelPage } from './ViewModelPage';
import { useViewModelEntity } from '@view-model/domain/view-model';
import { UserOperationModesProvider } from './UserOperationModesProvider';
import { LoginModal } from '@framework/ui/elements/LoginModal';
import { UserSurveyModal } from '@user/UserSurveyAnswer/UserSurveyModal';
import { useCurrentUserId } from '@framework/auth';
import { startPerfMonitor, stopPerfMonitor } from '@framework/utils';
import { useIsPerfDebugMode } from '@framework/utils/perfDebugMode';
import { useEffect } from 'react';
import { getDefaultStore } from 'jotai';
import { viewModelModelingFormatAtom } from './ViewModelModelingFormatAtom';

/**
 * `/view-models/<id>` 形式のURLでアクセスしたときに、
 * ビューモデルのページを表示するために必要な情報を取得・準備するラッパーコンポーネントです。
 *
 * @returns
 */
export const ViewModelPageRouteContent: React.FC = () => {
  const { t } = useTranslation();
  const isPerfDebugMode = useIsPerfDebugMode();

  useEffect(() => {
    if (!isPerfDebugMode) return;

    startPerfMonitor();
    return () => {
      stopPerfMonitor();
    };
  }, [isPerfDebugMode]);

  const { viewModelId } = useParams<{ viewModelId: string }>();
  const { viewModel, workspace, accessible, loading } = useViewModelEntity(viewModelId ?? '');

  const currentUserId = useCurrentUserId();

  useEffect(() => {
    if (!workspace) return;

    getDefaultStore().set(viewModelModelingFormatAtom, {
      modelingFormat: workspace.setting.modelingFormat ?? 'CJK',
    });
  }, [workspace]);

  // ローディング中
  if (loading) {
    return <FullScreenLoading />;
  }

  // 権限的にアクセスできない場合には、 Not Found ページを表示する
  if (!accessible) {
    return <NotFoundPage />;
  }

  // ワークスペース、ビューモデルのエンティティを取得できていれば、コンテンツを表示する。
  // 未取得の場合には、ログイン画面を表示する。
  return workspace && viewModel ? (
    <ActionLogEventParamsContextProvider
      groupId={viewModel.groupId}
      workspaceId={workspace.id}
      workspaceName={workspace.name.toString()}
      viewModelId={viewModelId}
      viewModelName={viewModel.name.toString()}
    >
      <UserOperationModesProvider>
        <ViewModelPage workspace={workspace} viewModel={viewModel} />
        {isOperationCalibrationEnabled() && <OperationCalibrationPanel />}
        {currentUserId && <UserSurveyModal groupId={viewModel.groupId} currentUserId={currentUserId} />}
      </UserOperationModesProvider>
    </ActionLogEventParamsContextProvider>
  ) : (
    <LoginModal
      isOpen={true}
      onClose={() => void 0}
      title={t('viewModel.page.loginRequired')}
      shouldCloseOnOverlayClick={true}
    />
  );
};
